/**
 * Retrieval del Día 4 — una tarjeta a la vez.
 *
 * NO es un mazo de flashcards. La respuesta está OCULTA hasta que el usuario
 * intenta, y antes de revelarla se le recuerda que rodee la idea en inglés.
 * Ese orden —situación → intento → circumlocución → ayuda— es el ejercicio;
 * invertirlo lo convertiría en reconocimiento, que es justo lo que ya sabe
 * hacer.
 *
 * El material sale de la INSTANTÁNEA de la sesión, no de una consulta nueva:
 * si edita el banco a mitad de sesión, lo de hoy no cambia debajo suyo.
 */

import { useState } from 'react';

import { NOMBRE_AREA } from '../../domain/modelo/areas';
import type { ElementoBanco } from '../../domain/modelo/banco';
import {
  NOMBRE_RESULTADO,
  RESULTADOS_PRACTICA,
  type ResultadoPractica,
} from '../../domain/reglas/retrieval';
import estilos from './Retrieval.module.css';

interface Props {
  elementos: ElementoBanco[];
  /** Resultados ya registrados en esta sesión. */
  resultados: Record<string, ResultadoPractica>;
  onPracticar: (id: string, resultado: ResultadoPractica) => void;
  /** Cuántos pide el curriculum, para poder decirlo cuando falten. */
  minimo: number;
}

export function Retrieval({ elementos, resultados, onPracticar, minimo }: Props) {
  const [revelados, setRevelados] = useState<Record<string, boolean>>({});
  const [indice, setIndice] = useState(0);

  if (elementos.length === 0) {
    return (
      <section className={estilos.bloque} aria-label="Material de retrieval">
        <h3 className={estilos.titulo}>Tu material de hoy</h3>
        <p className={estilos.vacio}>
          Todavía no hay material activo en el Learning Bank. No pasa nada y no hay que
          forzarlo: en la Semana 1 esto es lo normal.
        </p>
        <p className={estilos.vacio}>
          Usá las dificultades que guardaste en el baseline y, durante la conversación,
          andá capturando lo que te falte con la captura rápida desde Hoy.
        </p>
      </section>
    );
  }

  const elemento = elementos[Math.min(indice, elementos.length - 1)]!;
  const revelado = revelados[elemento.id] ?? false;
  const resultado = resultados[elemento.id];
  const practicados = elementos.filter((e) => resultados[e.id]).length;

  return (
    <section className={estilos.bloque} aria-label="Material de retrieval">
      <div className={estilos.cabecera}>
        <h3 className={estilos.titulo}>Tu material de hoy</h3>
        <span className={estilos.progreso}>
          {practicados} de {elementos.length}
        </span>
      </div>

      {elementos.length < minimo ? (
        <p className={estilos.aviso}>
          Hoy hay {elementos.length} en vez de {minimo}. Trabajá con lo que haya: es
          preferible a inventar material.
        </p>
      ) : null}

      <article className={estilos.tarjeta}>
        <div className={estilos.etiquetas}>
          {elemento.area ? (
            <span className={estilos.area}>{NOMBRE_AREA[elemento.area]}</span>
          ) : null}
          {elemento.vecesOcurrido > 1 ? (
            <span className={estilos.veces}>volvió a pasar ×{elemento.vecesOcurrido}</span>
          ) : null}
        </div>

        {elemento.contextoOriginal.trim() ? (
          <p className={estilos.contexto}>{elemento.contextoOriginal}</p>
        ) : (
          /* Sin contexto no hay nada desde donde recuperar, así que se dice
             en vez de dejar un hueco: el ejercicio sigue siendo válido, pero
             es recordar la corrección, no reconstruirla desde una situación. */
          <p className={estilos.sinContexto}>
            No anotaste el contexto de esta corrección. Intentá recordarla tal
            cual antes de mirar.
          </p>
        )}

        {!revelado ? (
          <>
            <p className={estilos.instruccion}>
              Intentá decirlo en inglés, en voz alta, antes de mirar.
            </p>
            <p className={estilos.circunlocucion}>
              Si no te sale, intentá explicarlo de otra manera en inglés. Rodear la idea
              es parte del ejercicio, no una trampa.
            </p>
            <button
              type="button"
              className={estilos.mostrar}
              onClick={() => setRevelados((previos) => ({ ...previos, [elemento.id]: true }))}
            >
              Mostrar
            </button>
          </>
        ) : (
          <>
            {elemento.correccionTarget.trim() ? (
              <div className={estilos.respuesta}>
                <span className={estilos.etiquetaRespuesta}>Cómo se dice</span>
                <p className={estilos.target}>{elemento.correccionTarget}</p>
              </div>
            ) : (
              <p className={estilos.sinTarget}>
                Este elemento no tiene una respuesta guardada: es un problema que
                anotaste, no una expresión. Usalo como tema para la conversación.
              </p>
            )}
            {elemento.notas?.trim() ? (
              <p className={estilos.notas}>{elemento.notas}</p>
            ) : null}

            <fieldset className={estilos.resultado}>
              <legend className={estilos.etiquetaRespuesta}>¿Cómo te fue?</legend>
              <div className={estilos.opciones}>
                {RESULTADOS_PRACTICA.map((r) => (
                  <button
                    key={r}
                    type="button"
                    className={
                      resultado === r
                        ? `${estilos.opcion} ${estilos.opcionActiva}`
                        : estilos.opcion
                    }
                    onClick={() => onPracticar(elemento.id, r)}
                  >
                    {NOMBRE_RESULTADO[r]}
                  </button>
                ))}
              </div>
            </fieldset>
          </>
        )}
      </article>

      <div className={estilos.navegacion}>
        <button
          type="button"
          className={estilos.navegar}
          onClick={() => setIndice((i) => Math.max(0, i - 1))}
          disabled={indice === 0}
        >
          Anterior
        </button>
        <span className={estilos.posicion}>
          {Math.min(indice, elementos.length - 1) + 1} / {elementos.length}
        </span>
        <button
          type="button"
          className={estilos.navegar}
          onClick={() => setIndice((i) => Math.min(elementos.length - 1, i + 1))}
          disabled={indice >= elementos.length - 1}
        >
          Siguiente
        </button>
      </div>
    </section>
  );
}
